import { supabase } from "@/lib/supabase";
import type { LeadTargetField } from "@/lib/leadFields";
import { recordActivity } from "@/services/activityLog";

export interface LeadImportRow {
  id: string;
  team_id: string;
  uploaded_by: string;
  filename: string;
  row_count: number;
  rows_inserted: number;
  rows_skipped: number;
  mapping: Record<string, LeadTargetField>;
  source_headers: string[];
  notes: string | null;
  created_at: string;
}

export interface CreateImportInput {
  team_id: string;
  uploaded_by: string;
  filename: string;
  row_count: number;
  rows_inserted: number;
  rows_skipped: number;
  mapping: Record<string, LeadTargetField>;
  source_headers: string[];
  notes?: string | null;
}

/** Creates a `lead_imports` row and returns the new id. */
export async function createLeadImport(input: CreateImportInput): Promise<string> {
  const { data, error } = await supabase
    .from("lead_imports")
    .insert({
      team_id: input.team_id,
      uploaded_by: input.uploaded_by,
      filename: input.filename,
      row_count: input.row_count,
      rows_inserted: input.rows_inserted,
      rows_skipped: input.rows_skipped,
      mapping: input.mapping,
      source_headers: input.source_headers,
      notes: input.notes ?? null,
    })
    .select("id")
    .single();
  if (error) throw error;
  return data.id as string;
}

// `listLeadImports` used to live here for an Imports-history sub-page that
// was never built — Phase 2A's Activity log + lead_imports row count
// (visible in the activity_log metadata) covered the use case. Removed to
// keep the service surface honest. Re-add when an actual import-history
// UI exists.

/** Patches the row counts on a previously-created import after the batched
 *  insert finishes. Best-effort — Import.tsx shouldn't reach for a raw
 *  supabase client to do this. Also fires the activity_log entry — we do it
 *  here (not in createLeadImport) because at this point the counts are
 *  finalized, so the log line reads "Imported 245 leads" instead of the
 *  pre-insert "Imported 0 leads" placeholder. */
export async function updateLeadImportCounts(
  importId: string,
  counts: { rows_inserted: number; rows_skipped: number },
  ctx?: { filename?: string },
): Promise<void> {
  const { error } = await supabase
    .from("lead_imports")
    .update({
      rows_inserted: counts.rows_inserted,
      rows_skipped: counts.rows_skipped,
    })
    .eq("id", importId);
  if (error) throw error;

  void recordActivity({
    kind: "lead_import",
    target_type: "lead_import",
    target_id: importId,
    summary: ctx?.filename
      ? `Imported ${counts.rows_inserted} leads from ${ctx.filename}`
      : `Imported ${counts.rows_inserted} leads (${counts.rows_skipped} skipped)`,
    metadata: {
      rows_inserted: counts.rows_inserted,
      rows_skipped: counts.rows_skipped,
      filename: ctx?.filename ?? null,
    },
  });
}
